/**
 * Primarily, exports function:
 *
 * <pre>
 *    // returns a console-like object - requires a prefix that should be unique across app
 *    const logger = createLogger({
 *      prefix: 'ModuleName::ComponentName' //  suggested use of prefix -  module is optional, but
 *                                          //  can be used to group components for log filtering
 *    })
 *    // ex usages:
 *    logger.debug('debug message', args)
 *    logger.log('log message', args)
 *    logger.info('info message', args)
 *    logger.warn('warn message', args)
 *    logger.error('error message', e)
 * </pre>
 *
 * Log can be configured via env variable:
 *
 * <pre>
 *   # if set to false, suppresses ALL logs except errors
 *   LOG_ENABLED=true
 *
 *   # one of: debug,log,info,warn,error
 *   LOG_LEVEL=debug
 *
 *   # if set to true, will output log config to console
 *   LOG_DIAGNOSTICS=false
 *
 *   # if blank, has no effect...
 *   # if a comma-delimited list of strings is provided, will 'whitelist' log statements, only printing those with 'prefix' as value from list
 *   LOG_WHITELIST=ModuleName::ComponentName1,ModuleName::ComponentName2
 *
 *   # if blank, has no effect...
 *   # if a comma-delimited list of strings is provided, will 'blacklist' log statements, not printing those with 'prefix' as value from list (takes precedence over whitelist)
 *   LOG_BLACKLIST=ModuleName::ComponentName1,ModuleName::ComponentName2
 * </pre>
 *
 * All env variables can be prefixed with NEXT_PUBLIC_ or REACT_APP_
 */

export enum LoggerLevel {
  // eslint-disable-next-line
  debug = 'debug',
  // eslint-disable-next-line
  log = 'log',
  // eslint-disable-next-line
  info = 'info',
  // eslint-disable-next-line
  warn = 'warn',
  // eslint-disable-next-line
  error = 'error'
}

export type Logger = (...data: any[]) => void
export type LoggerFactory = (level: LoggerLevel) => Logger
// eslint-disable-next-line
export type AppLogger = { [key in LoggerLevel]: Logger }

/**
 * Config object for global logging options
 */
export type LoggerCfg = {
  /**
   * Used to create an alternate logger object (function) to use instead of `console`
   */
  altLoggerFactory?: LoggerFactory,
  enabled: boolean
  logLevel: LoggerLevel
  whitelist: string[]
  blacklist: string[]
  diagnostics: boolean
  /**
   * If provided, adds prefix to all log statements (allows for filtering in dev console)
   */
  globalPrefix?: string
}

export type LoggerProps = {
  prefix: string
}

export enum LoggerEnv {
  // # if set to false, suppresses ALL logs except errors
  // eslint-disable-next-line
  LOG_ENABLED = 'LOG_ENABLED',

  // # one of: debug,log,info,warn,error
  // eslint-disable-next-line
  LOG_LEVEL = 'LOG_LEVEL',

  // # if set to true, will output log config to console
  // eslint-disable-next-line
  LOG_DIAGNOSTICS = 'LOG_DIAGNOSTICS',

  // # if blank, has no effect...
  // # if a comma-delimited list of strings is provided, will 'whitelist' log statements, only printing those with 'prefix' as value from list
  // eslint-disable-next-line
  LOG_WHITELIST = 'LOG_WHITELIST',

  // # if blank, has no effect...
  // # if a comma-delimited list of strings is provided, will 'blacklist' log statements, not printing those with 'prefix' as value from list (takes precedence over whitelist)
  // eslint-disable-next-line
  LOG_BLACKLIST = 'LOG_BLACKLIST'
}

// ===================================================================================================
// internals

const getLogEnvVar = (envVar: LoggerEnv) => {
  if (process.env[String(envVar)]) {
    return process.env[envVar]
  }
  if (process.env[`NEXT_PUBLIC_${String(envVar)}`]) {
    return process.env[`NEXT_PUBLIC_${String(envVar)}`]
  }
  if (process.env[`REACT_APP_${String(envVar)}`]) {
    return process.env[`REACT_APP_${String(envVar)}`]
  }
}

export const getLogLevel = (levelStr?: string, returnNull = false) => {
  const levelName = levelStr?.toLowerCase() as keyof typeof LoggerLevel
  const level = LoggerLevel[levelName]
  if (!level && returnNull) {
    return null
  }
  if (!level) {
    throw new Error('Invalid Log Level: ' + levelStr)
  }
  return level
}

export const getLoggerList = (list?: string) => list === 'null' ? []
  : (list || '')
      .split(/\s*,\s*/)
      .filter(str => str.length > 0)

// ===================================================================================================
// exported functions

const LOG_LEVELS = [
  LoggerLevel.debug,
  LoggerLevel.log,
  LoggerLevel.info,
  LoggerLevel.warn,
  LoggerLevel.error
]
/**
 * Returns a wrapper for console that adds log prefixing for filtering, and allows for
 * other centralized log manipulation/suppression/etc
 */
export const createLogger = (props: LoggerProps): AppLogger => {
  const { prefix } = props

  const isCallEnabled = (prefix: string, curr: Logger, level: LoggerLevel) => {
    if (level === LoggerLevel.error) {
      return true // always log errors
    }
    if (!loggerConfig.enabled) {
      loggerConfig.diagnostics && console.warn('DISABLED LOGGER (!LOG_ENABLED): ', {
        prefix,
        level
      })
      return false
    }

    if (loggerConfig.blacklist.length > 0) {
      const i = loggerConfig.blacklist.indexOf(prefix)
      const disabled = i >= 0
      if (disabled) {
        loggerConfig.diagnostics && console.warn('DISABLED LOGGER (blacklisted): ', {
          prefix,
          level
        })
        return false
      }
    }
    if (loggerConfig.whitelist.length > 0) {
      const i = loggerConfig.whitelist.indexOf(prefix)
      const enabled = i >= 0
      if (!enabled) {
        loggerConfig.diagnostics && console.warn('DISABLED LOGGER (non-whitelisted): ', {
          prefix,
          level
        })
      }
      return enabled
    }
    const enabledLevelVal = LOG_LEVELS.findIndex(l => l === loggerConfig.logLevel)
    const currentLevelVal = LOG_LEVELS.findIndex(l => l === level)
    const enabled = currentLevelVal >= enabledLevelVal
    if (!enabled) {
      loggerConfig.diagnostics && console.warn('DISABLED LOGGER (log levels): ', {
        prefix,
        level,
        currentLevelVal: LOG_LEVELS[currentLevelVal],
        enabledLevelVal: LOG_LEVELS[enabledLevelVal]
      })
    }
    return enabled
  }

  const getLogToUse = (logger: Logger, level: LoggerLevel): Logger => {
    const logToUse = loggerConfig.altLoggerFactory && loggerConfig.altLoggerFactory(level)
    return logToUse || logger
  }

  const getLogPrefix = ({
    prefix,
    level
  }: { prefix: string, level: LoggerLevel }) => {
    // quick n dirty formatted time-string
    const currTime = () => {
      const d = new Date()
      const f = (num: number, dig = 2) => {
        return String(num).padStart(dig, '0')
      }
      const h = d.getHours()
      const m = d.getMinutes()
      const s = d.getSeconds()
      const ms = d.getMilliseconds()

      return `${f(h)}:${f(m)}:${f(s)}:${f(ms, 3)}`
    }
    return `${loggerConfig.globalPrefix ?? ''}${currTime()} - (${level.toUpperCase()}) ${prefix}`
  }

  type MessageSepAndArgs = {
    message: string
    sep?: string
    args: []
  }

  /**
   * Parses logger args into a:
   * <ul>
   *   <li><b>message</b>: the logger message string (may be empty)</li>
   *   <li><b>sep</b>: the separator string (will be empty if message is empty)</li>
   *   <li><b>args</b>: the remaining args to send to logger (may be null or empty array)</li>
   * </ul>
   * @param data
   */
  const getMessageSepAndArgs = (...data: any[]): MessageSepAndArgs => {
    if (data.length === 0) {
      return {
        message: '',
        args: []
      }
    }
    const [arg1, ...rest] = data
    let message, args
    if (typeof arg1 === 'string') {
      message = arg1
      args = rest
    } else {
      message = ''
      args = data
    }
    args = args.length === 1 ? args[0] : args.flat()
    const sep = message.length > 0 ? ' :: ' : '' // add non-blank separator for non-blank message
    return {
      message,
      sep,
      args
    }
  }

  return Object.keys(LoggerLevel)
    .map(level => (console as any)[level])
    .reduce((prev, logger) => {
      return {
        ...prev,
        [logger.name]: (...data: any[]) => {
          const level = getLogLevel(logger.name) ?? LoggerLevel.warn
          if (isCallEnabled(prefix, logger, level)) {
            const logPrefix = getLogPrefix({
              prefix,
              level
            })
            const logToUse = getLogToUse(logger, level)
            const {
              message,
              sep,
              args
            } = getMessageSepAndArgs(...data)
            if (!args || args.length === 0) {
              logToUse(`${logPrefix}${sep}${message}`)
            } else {
              logToUse(`${logPrefix}${sep}${message}`, args)
            }
          }
        }
      }
    }, { ...console })
}

/**
 * Allows setting global logger config (primarily used for injection during testing)
 *
 * @see LoggerCfg
 */
export const configLogger = (cfg: Partial<LoggerCfg>) => {
  loggerConfig = createLoggerConfig(cfg, false)
}

// ===================================================================================================
// exported functions

const createLoggerConfig = (cfg?: Partial<LoggerCfg>, useProcess = true) => {
  const IS_PROD = process.env.NODE_ENV === 'production'
  const LOG_ENABLED = getLogEnvVar(LoggerEnv.LOG_ENABLED) !== 'false'
  const LOG_LEVEL = getLogEnvVar(LoggerEnv.LOG_LEVEL)
  const LOG_DIAGNOSTICS = getLogEnvVar(LoggerEnv.LOG_DIAGNOSTICS) === 'true'
  const ENABLED_LOGS = [
    ...getLoggerList(getLogEnvVar(LoggerEnv.LOG_WHITELIST)),
  ]
  const DISABLED_LOGS = [
    ...getLoggerList(getLogEnvVar(LoggerEnv.LOG_BLACKLIST)),
  ]

  const fromProcess: LoggerCfg = {
    diagnostics: LOG_DIAGNOSTICS,
    logLevel: getLogLevel(LOG_LEVEL, true) ?? LoggerLevel.info,
    whitelist: ENABLED_LOGS,
    blacklist: DISABLED_LOGS,
    enabled: LOG_ENABLED
  }

  const config: LoggerCfg = useProcess
    ? {
      // allow settings from process.env to override incoming config
        ...cfg,
        ...fromProcess
      } : {
      // do NOT allow settings from process.env to override incoming config
        ...fromProcess,
        ...cfg
      }

  !IS_PROD && console.log('Logger config: ', config)
  config.diagnostics && console.log('Logger config from env: ', {
    FROM_PROCESS: {
      LOG_ENABLED: process.env.LOG_ENABLED,
      LOG_LEVEL: process.env.LOG_LEVEL,
      LOG_DIAGNOSTICS: process.env.LOG_DIAGNOSTICS,
      LOG_WHITELIST: process.env.LOG_WHITELIST,
      LOG_BLACKLIST: process.env.LOG_BLACKLIST,
      NEXT_PUBLIC_LOG_WHITELIST: process.env.NEXT_PUBLIC_LOG_WHITELIST,
      NEXT_PUBLIC_LOG_ENABLED: process.env.NEXT_PUBLIC_LOG_ENABLED,
      NEXT_PUBLIC_LOG_BLACKLIST: process.env.NEXT_PUBLIC_LOG_BLACKLIST,
      REACT_APP_LOG_WHITELIST: process.env.REACT_APP_LOG_WHITELIST,
      REACT_APP_LOG_ENABLED: process.env.REACT_APP_LOG_ENABLED,
      REACT_APP_LOG_BLACKLIST: process.env.REACT_APP_LOG_BLACKLIST,
    }
  })

  return config
}

// may be set at runtime via configLogger
let loggerConfig: LoggerCfg
loggerConfig = createLoggerConfig()
